import {
	Building2,
	CalendarDays,
	CalendarRange,
	LayoutGrid,
} from "lucide-react";
import { useMemo } from "react";
import type { ChartInterval } from "@/adapters/api";
import { ViewToggle, type ViewToggleOption } from "@/components/view-toggle";
import { useActiveCanteiro } from "@/features/canteiros/hooks/use-active-canteiro.ts";
import { AttendancePanel } from "@/features/dashboard/components/attendance";
import { ClientHero } from "@/features/dashboard/components/client-hero";
import { InProgressStages } from "@/features/dashboard/components/in-progress-stages";
import { KpiRow } from "@/features/dashboard/components/kpi-row";
import { MeasurementHistory } from "@/features/dashboard/components/measurement-history";
import { Milestones } from "@/features/dashboard/components/milestones";
import { NoBaseline } from "@/features/dashboard/components/no-baseline";
import { Panel } from "@/features/dashboard/components/panel";
import { PortfolioKpis } from "@/features/dashboard/components/portfolio-kpis";
import { PortfolioTable } from "@/features/dashboard/components/portfolio-table";
import { PresencePanel } from "@/features/dashboard/components/presence-panel";
import { ProgressCurve } from "@/features/dashboard/components/progress-curve";
import { RecentPosts } from "@/features/dashboard/components/recent-posts";
import { StageBars } from "@/features/dashboard/components/stage-bars";
import { StageStatusPanel } from "@/features/dashboard/components/stage-status";
import { WeatherCard } from "@/features/dashboard/components/weather-card";
import { useAttendance } from "@/features/dashboard/hooks/use-attendance.ts";
import { useConstructionProgress } from "@/features/dashboard/hooks/use-construction-progress.ts";
import { useConstructionSchedule } from "@/features/dashboard/hooks/use-construction-schedule.ts";
import { useMeasurementHistory } from "@/features/dashboard/hooks/use-measurement-history.ts";
import { usePortfolio } from "@/features/dashboard/hooks/use-portfolio.ts";
import { useRecentPosts } from "@/features/dashboard/hooks/use-recent-posts.ts";
import { useReferenceDate } from "@/features/dashboard/hooks/use-reference-date.ts";
import { useStageStatus } from "@/features/dashboard/hooks/use-stage-status.ts";
import type { DashboardScope } from "@/features/dashboard/types.ts";
import { formatLongDate } from "@/features/dashboard/utils/format.ts";
import {
	OBRA_PANELS,
	resolvePanels,
	toRoleName,
} from "@/features/dashboard/utils/layout.ts";
import { nextMilestone } from "@/features/dashboard/utils/milestone.ts";
import {
	inProgressLeaves,
	rootStages,
} from "@/features/dashboard/utils/schedule.ts";
import { NewPostDialog } from "@/features/feed/components/new-post-dialog";
import { PendingBanner } from "@/features/presence/components/pending-banner";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { useMe } from "@/hooks/use-me.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useDashboardCurveInterval } from "@/store/use-dashboard-curve-interval.ts";
import { useDashboardScope } from "@/store/use-dashboard-scope.ts";

const SCOPE_OPTIONS: ViewToggleOption<DashboardScope>[] = [
	{ value: "obra", label: "Obra", icon: Building2 },
	{ value: "portfolio", label: "Canteiro", icon: LayoutGrid },
];

const CURVE_INTERVAL_OPTIONS: ViewToggleOption<ChartInterval>[] = [
	{ value: "monthly", label: "Mensal", icon: CalendarRange },
	{ value: "weekly", label: "Semanal", icon: CalendarDays },
];

export function DashboardPage() {
	const me = useMe();
	const { has } = usePermissions();
	const { obra } = useActiveObra();
	const { canteiro } = useActiveCanteiro();
	const referenceDate = useReferenceDate();
	const scope = useDashboardScope((state) => state.scope);
	const setScope = useDashboardScope((state) => state.setScope);
	const curveInterval = useDashboardCurveInterval((state) => state.interval);
	const setCurveInterval = useDashboardCurveInterval(
		(state) => state.setInterval,
	);

	const role = toRoleName(me.data?.account?.role?.name);
	const panels = new Set(resolvePanels(OBRA_PANELS, { has, role }));
	const canSwitchScope = has("charts:portfolio:read");
	const isObraScope = scope === "obra" || !canSwitchScope;

	const progress = useConstructionProgress({
		constructionId: obra?.id,
		referenceDate,
		enabled:
			isObraScope &&
			(panels.has("kpi") ||
				panels.has("progress-curve") ||
				panels.has("stage-bars") ||
				panels.has("client-hero")),
	});
	const curve = useConstructionProgress({
		constructionId: obra?.id,
		referenceDate,
		interval: curveInterval,
		enabled: isObraScope && panels.has("progress-curve"),
	});
	const schedule = useConstructionSchedule({
		constructionId: obra?.id,
		enabled:
			isObraScope &&
			(panels.has("kpi") ||
				panels.has("client-hero") ||
				panels.has("milestones") ||
				panels.has("in-progress-stages")),
	});
	const stageStatus = useStageStatus({
		constructionId: obra?.id,
		referenceDate,
		enabled: isObraScope && panels.has("stage-status"),
	});
	const measurements = useMeasurementHistory({
		constructionId: obra?.id,
		referenceDate,
		enabled: isObraScope && panels.has("measurement-history"),
	});
	const attendance = useAttendance({
		worksiteId: canteiro?.id,
		referenceDate,
		enabled: isObraScope && panels.has("attendance"),
	});
	const posts = useRecentPosts({
		constructionId: obra?.id,
		visibility: role === "cliente" ? "public" : undefined,
		enabled: isObraScope && panels.has("recent-posts"),
	});
	const portfolio = usePortfolio({
		worksiteId: canteiro?.id,
		referenceDate,
		enabled: !isObraScope,
	});

	const scheduleItems = schedule.data?.items;
	const milestone = useMemo(
		() => (scheduleItems ? nextMilestone(scheduleItems, referenceDate) : null),
		[scheduleItems, referenceDate],
	);
	const milestoneStages = useMemo(
		() => (scheduleItems ? rootStages(scheduleItems) : undefined),
		[scheduleItems],
	);
	const runningStages = useMemo(
		() => (scheduleItems ? inProgressLeaves(scheduleItems) : undefined),
		[scheduleItems],
	);

	const noBaseline = progress.data?.no_baseline === true;
	const weatherCard = panels.has("weather") ? <WeatherCard /> : undefined;
	const title = isObraScope
		? (obra?.name ?? "Dashboard")
		: (canteiro?.name ?? "Canteiro");

	return (
		<div className="dashboard-page">
			<header className="page-header">
				<div>
					<p className="dashboard-page__date">
						{formatLongDate(referenceDate)}
					</p>
					<h1 className="page-title">{title}</h1>
				</div>
				{canSwitchScope && (
					<div className="page-header__actions">
						<ViewToggle
							options={SCOPE_OPTIONS}
							value={scope}
							onChange={setScope}
						/>
					</div>
				)}
			</header>

			{!isObraScope ? (
				<div className="dashboard-grid">
					<div className="dashboard-grid__full">
						<PortfolioKpis
							data={portfolio.data}
							isPending={portfolio.isPending}
							isError={portfolio.isError}
						/>
					</div>
					<div className="dashboard-grid__full">
						<Panel
							title="Obras do canteiro"
							description="Da mais atrasada para a mais adiantada"
						>
							<PortfolioTable
								data={portfolio.data}
								isPending={portfolio.isPending}
								isError={portfolio.isError}
							/>
						</Panel>
					</div>
				</div>
			) : (
				<div className="dashboard-grid">
					{panels.has("client-hero") && (
						<div className="dashboard-grid__full">
							<ClientHero
								name={obra?.name ?? "Sua obra"}
								data={progress.data}
								isPending={progress.isPending}
								isError={progress.isError}
								span={schedule.data?.span}
								isSpanPending={schedule.isPending}
							/>
						</div>
					)}

					{panels.has("presence") && (
						<div className="dashboard-grid__full">
							<PresencePanel />
						</div>
					)}

					{panels.has("kpi") && (
						<div className="dashboard-grid__full">
							<KpiRow
								data={progress.data}
								isPending={progress.isPending}
								isError={progress.isError}
								milestone={milestone}
								isMilestonePending={schedule.isPending}
								trailing={weatherCard}
							/>
						</div>
					)}

					{!panels.has("kpi") && weatherCard && (
						<div className="dashboard-grid__full dashboard-weather-row">
							{weatherCard}
						</div>
					)}

					{has("time_entries:list_team") && (
						<PendingBanner compact className="dashboard-grid__full" />
					)}

					{noBaseline
						? (panels.has("progress-curve") || panels.has("stage-bars")) && (
								<Panel title="Avanço da obra" wide>
									<NoBaseline />
								</Panel>
							)
						: null}

					{!noBaseline && panels.has("progress-curve") && (
						<Panel
							title="Curva S"
							description="Avanço acumulado planejado e realizado"
							action={
								<ViewToggle
									options={CURVE_INTERVAL_OPTIONS}
									value={curveInterval}
									onChange={setCurveInterval}
								/>
							}
							wide
						>
							<ProgressCurve
								data={curve.data}
								isPending={curve.isPending}
								isError={curve.isError}
							/>
						</Panel>
					)}

					{panels.has("stage-status") && (
						<Panel
							title="Situação das atividades"
							description="Distribuição das etapas e o que está atrasado"
						>
							<StageStatusPanel
								data={stageStatus.data}
								isPending={stageStatus.isPending}
								isError={stageStatus.isError}
							/>
						</Panel>
					)}

					{!noBaseline && panels.has("stage-bars") && (
						<Panel
							title="Avanço por etapa"
							description="Planejado contra realizado, com detalhamento por etapa"
							wide
						>
							<StageBars
								constructionId={obra?.id}
								referenceDate={referenceDate}
								rootData={progress.data}
								isPending={progress.isPending}
								isError={progress.isError}
							/>
						</Panel>
					)}

					{panels.has("in-progress-stages") && (
						<Panel
							title="Etapas em andamento"
							description={
								role === "almoxarife"
									? "Pelo fim previsto mais próximo"
									: "O que está aberto nesta obra"
							}
						>
							<InProgressStages
								variant={role === "almoxarife" ? "list" : "count"}
								stages={runningStages}
								isPending={schedule.isPending}
								isError={schedule.isError}
							/>
						</Panel>
					)}

					{panels.has("milestones") && (
						<Panel
							title="Marcos da obra"
							description="As grandes etapas e onde elas estão"
						>
							<Milestones
								stages={milestoneStages}
								isPending={schedule.isPending}
								isError={schedule.isError}
							/>
						</Panel>
					)}

					{panels.has("measurement-history") && (
						<Panel
							title="Histórico de medições"
							description="Medições registradas e avanço obtido"
						>
							<MeasurementHistory
								data={measurements.data}
								isPending={measurements.isPending}
								isError={measurements.isError}
							/>
						</Panel>
					)}

					{panels.has("attendance") && (
						<Panel
							title="Presença no canteiro"
							description="Pessoas que bateram ponto por dia"
						>
							<AttendancePanel
								data={attendance.data}
								isPending={attendance.isPending}
								isError={attendance.isError}
							/>
						</Panel>
					)}

					{panels.has("new-post") && (
						<Panel
							title="Registrar relato"
							description="Conte o que aconteceu na obra hoje"
						>
							<NewPostDialog />
						</Panel>
					)}

					{panels.has("recent-posts") && (
						<Panel title="Últimos relatos">
							<RecentPosts
								posts={posts.data}
								isPending={posts.isPending}
								isError={posts.isError}
							/>
						</Panel>
					)}
				</div>
			)}
		</div>
	);
}
